/**
 * Fetch a server-rendered receipt HTML URL (requires Bearer auth) and print
 * it via a hidden iframe, the same technique printReceiptNode uses.
 *
 * Using an iframe avoids the popup-blocker surface entirely and sidesteps
 * Chrome's restriction on window.print() called from about:blank popup
 * scripts. The blob URL is same-origin with the creating page so
 * iframe.contentWindow.print() prints the iframe content, not the parent.
 */
export async function printReceiptUrl(
  url: string,
  accessToken: string | null,
  _title: string,
): Promise<void> {
  let html: string;
  try {
    const resp = await fetch(url, {
      cache: "no-store",
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        Accept: "text/html",
      },
    });
    if (!resp.ok) {
      throw new Error(`receipt fetch ${resp.status}`);
    }
    html = await resp.text();
  } catch {
    alert("Could not load receipt. Please try again.");
    return;
  }

  const blob = new Blob([html], { type: "text/html; charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:0;width:800px;height:1200px;border:0;visibility:hidden";
  document.body.appendChild(iframe);

  const cleanup = () => {
    iframe.remove();
    URL.revokeObjectURL(blobUrl);
  };

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(cleanup, 3000);
    }
  };

  iframe.src = blobUrl;
}

/**
 * Print the contents of a DOM node via an offscreen iframe.
 *
 * Inlines all same-origin stylesheet rules as raw cssText so the print
 * layout is fully resolved before print() fires. Cross-origin sheets
 * fall back to a <link> tag.
 */
export function printReceiptNode(node: HTMLElement, title: string): void {
  let inlineCss = "";
  const linkFallbacks: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) {
        inlineCss += rule.cssText + "\n";
      }
    } catch {
      if (sheet.href) {
        linkFallbacks.push(`<link rel="stylesheet" href="${sheet.href}" />`);
      }
    }
  }

  const html =
    `<!doctype html><html><head><meta charset="utf-8" /><title>${title}</title>` +
    linkFallbacks.join("") +
    `<style>${inlineCss}</style></head><body>${node.innerHTML}</body></html>`;

  const blob = new Blob([html], { type: "text/html; charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:0;width:800px;height:1200px;border:0;visibility:hidden";
  document.body.appendChild(iframe);

  const cleanup = () => {
    iframe.remove();
    URL.revokeObjectURL(blobUrl);
  };

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(cleanup, 3000);
    }
  };

  iframe.src = blobUrl;
}
